/**
 * Všechna volání backendu musí jít přes tento wrapper.
 * Zde je customerUuid vkládáno jako path variable do URL pro volání REST z backendu.
 *
 * Je inicializováno v ../main.js
 */
import axios from "axios";
import NProgress from "nprogress";
import "nprogress/nprogress.css";

// API Path nesmí být uvozena ani zakončena lomítkem
const API_PATH_ORGANIZACE = "organizace";
const API_PATH_UVOD = "uvod";
const API_PATH_CENIK = "cenik";
const API_PATH_LOGO = "logo";
const API_PATH_KONTAKTY = "kontakty";
const API_PATH_PODMINKY = "podminky";
const API_PATH_OCHRANA = "ochrana";
const API_PATH_PLATBA = "platba";
const API_PATH_STVRZENKA = "stvrzenka";

const API_PATH_POPLATKY_NEUHRAZENE = "poplatky/neuhrazene";
const API_PATH_PLATBY_UZIVATELE = "platby";
const API_PATH_PLATBY_DISABLED = "platby/disabled";
const API_PATH_PLATBA_UZIVATELE = "platba";

const API_PATH_POPLATEK_NEUHRAZENY_BY_ID = API_PATH_POPLATKY_NEUHRAZENE + "/id";
const API_PATH_POPLATKY_NEUHRAZENE_BY_IDS = API_PATH_POPLATKY_NEUHRAZENE + "/ids";

const API_PATH_TESTOVACI = "testovaci";
const API_PATH_TESTOVACI_S_LOGINEM = "testovaci-s-loginem";
const API_PATH_TEST_LOGIN = "auth/testlogin";
const API_PATH_TEST_LOGOUT = "auth/testlogout";
const API_PATH_AUTHENTICATION_STATUS = "auth/authentication-status";
const API_PATH_AUTHENTICATED_USER = "auth/authenticated-user";

const API_PATH_HAS_MENU_FORMS = "menu/hasforms";
const API_PATH_MENU_FORMS = "menu/forms";
const API_PATH_MENU_FORM = "menu/form";
const API_PATH_MENU_FORM_POLOZKY = "menu/form/polozky";
const API_PATH_MENU_FORM_POLOZKY_DEFAULTS = "menu/form/polozky/defaults";
const API_PATH_MENU_FORM_ZADOST = "menu/form/zadost";
const API_PATH_MENU_FORM_OPIS = "menu/form/opis";
const API_PATH_MENU_FORM_ZADOST_NAPOJENO = "menu/form/zadost/napojeno";
const API_PATH_MENU_FORM_DLAZDICE = "menu/form/dlazdice";

const API_PATH_CISELNIKY_PSI = "ciselniky/psi";
const API_PATH_CISELNIKY_UZIVATELSKE1 = "ciselniky/uzivatelsky1";
const API_PATH_CISELNIKY_UZIVATELSKE2 = "ciselniky/uzivatelsky2";
const API_PATH_CISELNIKY_UZIVATELSKE3 = "ciselniky/uzivatelsky3";

const API_PATH_CHECK_LICENCE_FORMULARU = "check_licence_formularu";
const API_PATH_LOG_POWEB_ERROR = "log_poweb_error";
const API_PATH_MAINTENANCE_STATUS = "maintenance-status";
const API_PATH_MAINTENANCE_MESSAGE = "maintenance-message";

var vuerouter;
var isLoggingError = false;

export const BackendPlugin = {
  install(Vue, customerUuid, router) {
    Vue.prototype.$backend = new BackendAxiosWrapper(customerUuid);
    vuerouter = router;
  },
};

// Vuex
import { store } from "../store";

class BackendAxiosWrapper {
  constructor(customerUuid) {
    var count = 0; // uchovává počet spuštěných requestů, pro potřeby zobrazení progressu

    this.customerUuid = customerUuid;
    // přidat interceptor, který přidá customerUuid do header každého requestu
    axios.interceptors.request.use(
      req => {
        req.headers.common["customerUuid"] = customerUuid;
        console.debug("[customerUuid] " + customerUuid);
        console.debug("[backend] " + req.method + " " + req.url);
        count++;
        if (count > 0) NProgress.start();
        return req;
      },
      function(error) {
        var status = (error != null && error.response && error.response.status) ? error.response.status : "";
        var data = (error != null && error.response && error.response.data) ? error.response.data : "";
        var mezera1 = status != "" || data != "" ? " " : "";
        var mezera2 = status != "" && data != "" ? " " : "";
        console.error("Axios req. Error" + mezera1 + status + mezera2 + data + ":");
        console.error(error);
        store.dispatch("setHtmlError", error);

        if (count > 0) count--;
        if (count == 0) NProgress.done();

        return Promise.reject(error);
      },
    );

    axios.interceptors.response.use(
      function(response) {
        if (count > 0) count--;
        if (count == 0) NProgress.done();
        return response;
      },
      function(error) {
        var status = (error != null && error.response && error.response.status) ? error.response.status : "";
        var data = (error != null && error.response && error.response.data) ? error.response.data : "";
        var mezera1 = status != "" || data != "" ? " " : "";
        var mezera2 = status != "" && data != "" ? " " : "";
        console.error("Axios resp. Error" + mezera1 + status + mezera2 + data + ":");
        console.error(error);
        store.dispatch("setHtmlError", error);

        if (count > 0) count--;
        if (count == 0) NProgress.done();

        // přesměrovat na view Error404 pokud API vrátilo 404
        if (error.response.status === 404) {
          vuerouter.push({ name: "error404" });
        }
        return Promise.reject(error);
      },
    );
  }

  /**
   * Vrací parametr schématu z KEO4.
   *
   * @param {*} resultConsumer
   */
  getOrganizace(resultConsumer) {
    this.getFromPath(API_PATH_ORGANIZACE, resultConsumer, false);
  }

  /**
   * Vrací úvodní text organizace.
   * @param {*} resultConsumer
   */
  getUvod(resultConsumer) {
    this.getFromPath(API_PATH_UVOD, resultConsumer, false);
  }

  /**
   * Vrací kontakty organizace.
   * @param {*} resultConsumer
   */
  getKontakty(resultConsumer) {
    this.getFromPath(API_PATH_KONTAKTY, resultConsumer, false);
  }

  /**
   * Vrací obchodní podmínky.
   * @param {*} resultConsumer
   */
  getObchodniPodminky(resultConsumer) {
    this.getFromPath(API_PATH_PODMINKY, resultConsumer, false);
  }

  /**
   * Vrací ochranu osobních údajů.
   * @param {*} resultConsumer
   */
  getOchranaUdaju(resultConsumer) {
    this.getFromPath(API_PATH_OCHRANA, resultConsumer, false);
  }

  /**
   * Vrací neuhrazené poplatky z KEO4.
   *
   * @param {*} resultConsumer
   * @param {*} count
   * @param {*} fromIndex
   */
  getPoplatkyNeuhrazene(resultConsumer, count, fromIndex, excludedIds) {
    var path =
      API_PATH_POPLATKY_NEUHRAZENE +
      "?count=" +
      count +
      "&fromIndex=" +
      fromIndex +
      "&excludedIds=" +
      excludedIds;
    this.getFromPath(path, resultConsumer, true);
  }

  getPlatbyUzivatele(resulConsumer, rok) {
    var path = API_PATH_PLATBY_UZIVATELE + "?rok=" + rok;
    this.getFromPath(path, resulConsumer, true);
  }

  getPlatbaById(resulConsumer, id) {
    var path = API_PATH_PLATBA_UZIVATELE + "?id=" + id;
    this.getFromPath(path, resulConsumer, true);
  }

  /**
   * Vrací neuhrazené poplatky z KEO4 dle jejich ids.
   *
   * @param {*} resultConsumer
   * @param {*} count
   * @param {*} fromIndex
   */
  getPoplatekNeuhrazenyById(resultConsumer, id) {
    var path =
      API_PATH_POPLATEK_NEUHRAZENY_BY_ID +
      "?id=" + id;
    this.getFromPath(path, resultConsumer, true);
  }

  /**
   * Vrací neuhrazené poplatky z KEO4 dle jejich ids.
   *
   * @param {*} resultConsumer
   * @param {*} count
   * @param {*} fromIndex
   */
  getPoplatkyNeuhrazeneByIds(resultConsumer, count, fromIndex, ids) {
    var path =
      API_PATH_POPLATKY_NEUHRAZENE_BY_IDS +
      "?count=" +
      count +
      "&fromIndex=" +
      fromIndex +
      "&ids=" +
      ids;
    this.getFromPath(path, resultConsumer, true);
  }

  checkLicenceFormularu(resultConsumer, vcetneSsl ) {
    this.getFromPathWithParams(API_PATH_CHECK_LICENCE_FORMULARU, vcetneSsl, resultConsumer, null, true);
  }

  /**
   * Vrací status údržby
   * @param {*} resultConsumer
   */
  getMaintenanceStatus(resultConsumer) {
    this.getFromPath(API_PATH_MAINTENANCE_STATUS, resultConsumer, false);
  }

  /**
   * Vrací popis údržby
   * @param {*} resultConsumer
   */
  getMaintenanceMessage(resultConsumer) {
    this.getFromPath(API_PATH_MAINTENANCE_MESSAGE, resultConsumer, false);
  }

  isTestovaci(resultConsumer) {
    this.getFromPath(API_PATH_TESTOVACI, resultConsumer, false);
  }

  isTestovaciSLoginem(resultConsumer) {
    this.getFromPath(API_PATH_TESTOVACI_S_LOGINEM, resultConsumer, false);
  }

  testLogin( testLoginParameters, resultConsumer, errorConsumer) {
    this.getFromPathWithParams(
        API_PATH_TEST_LOGIN,
        testLoginParameters,
        resultConsumer,
        errorConsumer,
        false
    );
  }

  testLogout(resultConsumer) {
    this.getFromPath(API_PATH_TEST_LOGOUT, resultConsumer, false);
  }

  /**
   * Vrací status autentizace
   * @param {*} resultConsumer
   */
  getAuthenticationStatus(resultConsumer) {
    this.getFromPath(API_PATH_AUTHENTICATION_STATUS, resultConsumer, false);
  }

  /**
   * Vrací informace o autentizovaném uživateli
   * @param {*} resultConsumer
   */
  getAuthnetictedUser(resultConsumer) {
    this.getFromPath(API_PATH_AUTHENTICATED_USER, resultConsumer, true);
  }

  postPlatba(data, resultConsumer, errorConsumer ) {
    this.postToPathSecure(API_PATH_PLATBA, data, resultConsumer, errorConsumer);
  }

  aktualizacePlatby(id, email, resultConsumer) {
    this.getFromPathWithParams(
      API_PATH_PLATBA + "/" + id,
      email,
      resultConsumer,
      null,
      true,
    );
  }


  getPlatbaAsResponseById(resultConsumer, id) {
    this.getFromPath(
        API_PATH_STVRZENKA + "/" + id,
      resultConsumer,
      true,
    );
  }

  /**
   * Stáhne ceník a vyvolá dialog prohlížeče pro otevření/uložení souboru.
   */
  getCenik() {
    axios({
      url: "/" + process.env.VUE_APP_API_OPEN_URL + API_PATH_CENIK,
      method: "GET",
      responseType: "blob",
    }).then(response => {
      if (response.data.size > 0) {
        var fileURL = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.target = "_blank";
        fileLink.setAttribute("open", "cenik.pdf");
        fileLink.setAttribute("aria-label", "cenik.pdf");
        document.body.appendChild(fileLink);

        fileLink.click();
      }
    });
  }

  getLogo(resultConsumer) {
    axios({
      url: "/" + process.env.VUE_APP_API_OPEN_URL + API_PATH_LOGO,
      method: "GET",
      responseType: "blob",
    }).then(resultConsumer);
  }

  /**
   * Zavolá zadanou cestu a zpracuje výsledek
   * @param {*} path
   * @param {*} resultConsumer
   * @param {*} sec - zda se předaná cesta volá v rámci zabezpečeného API
   */
  getFromPath(path, resultConsumer, sec) {
    this.getFromPathWithParams(path, {}, resultConsumer, null, sec);
  }

  /**
   * Zavolá zadanou cestu a zpracuje výsledek
   * @param {*} path
   * @param {*} queryParams - query params pro get
   * @param {*} resultConsumer
   * @param {*} sec - zda se předaná cesta volá v rámci zabezpečeného API
   */
  getFromPathWithParams(path, queryParams, resultConsumer, errorConsumer, sec) {
    var api = sec
      ? process.env.VUE_APP_API_SEC_URL
      : process.env.VUE_APP_API_OPEN_URL;
    console.debug(
      "[backend] sending GET req to: ",
      api + path,
      ", params: ",
      queryParams,
    );
    axios
        .get("/" + api + path, {params: queryParams})
        .then(resultConsumer, errorConsumer)
        .catch(error => {
            this.logError( "Chyba nacteni", error, path);
        });
  }

  postToPathSecure(path, data, resultConsumer, errorConsumer){
      this.postToPath(path, data, resultConsumer, errorConsumer, true);
  }

  postToPath(path, data, resultConsumer, errorConsumer, sec) {
    var api = sec
        ? process.env.VUE_APP_API_SEC_URL
        : process.env.VUE_APP_API_OPEN_URL;

    console.debug(
      "[backend] sending POST req to: ",
        api + path,
    );
    axios
      .post("/" + api + path, data)
      .then(resultConsumer, errorConsumer)
      .catch(error => {
        this.logError( "Chyba odeslani", error, path);
      });
  }

  existsMenuForms(resultConsumer) {
    this.getFromPath(API_PATH_HAS_MENU_FORMS, resultConsumer, false);
  }

  /**
   * Vrací polozky do menu.
   */
  getMenuForms(resultConsumer) {
    this.getFromPath(API_PATH_MENU_FORMS, resultConsumer, true);
  }

  getMenuFormById(resulConsumer, id) {
    var path = API_PATH_MENU_FORM + "?id=" + id;
    this.getFromPath(path, resulConsumer, true);
  }

  getMenuFormPolozkyByFormId(resulConsumer, id) {
    var path = API_PATH_MENU_FORM_POLOZKY + "?id=" + id;
    this.getFromPath(path, resulConsumer, true);
  }

  getDefaultsPolozekByFormId(resulConsumer, id) {
    var path = API_PATH_MENU_FORM_POLOZKY_DEFAULTS + "?id=" + id;
    this.getFromPath(path, resulConsumer, true);
  }

  getPlemenaPsu(resultConsumer) {
    this.getFromPath(API_PATH_CISELNIKY_PSI, resultConsumer, true);
  }

  getUzivatelskeCiselniky1(resultConsumer) {
    this.getFromPath(API_PATH_CISELNIKY_UZIVATELSKE1, resultConsumer, true);
  }

  getUzivatelskeCiselniky2(resultConsumer) {
    this.getFromPath(API_PATH_CISELNIKY_UZIVATELSKE2, resultConsumer, true);
  }

  getUzivatelskeCiselniky3(resultConsumer) {
    this.getFromPath(API_PATH_CISELNIKY_UZIVATELSKE3, resultConsumer, true);
  }

  postVyplnenyFormular( data, resultConsumer, errorConsumer) {
    this.postToPathSecure(API_PATH_MENU_FORM_ZADOST, data, resultConsumer, errorConsumer);
  }
  postVyplnenyFormularProOpis( data, resultConsumer, errorConsumer) {
    this.postToPathSecure(API_PATH_MENU_FORM_OPIS, data, resultConsumer, errorConsumer);
  }

  isZadostNapojenaNaSsl(resultConsumer) {
    this.getFromPath(API_PATH_MENU_FORM_ZADOST_NAPOJENO, resultConsumer, false);
  }

  isDlazdiceUFormularu(resultConsumer) {
    this.getFromPath(API_PATH_MENU_FORM_DLAZDICE, resultConsumer, false);
  }

  isDisablePlatby(resultConsumer) {
    this.getFromPath(API_PATH_PLATBY_DISABLED, resultConsumer, false);
  }

  logError(prefix, error, path) {
      this.logPowebError(prefix, error.message, path, error.stack);
  }

  logPowebError(prefix, error, path, stacktrace) {
    console.log("Logovani chyby: ", prefix, error, path, stacktrace);

    if (isLoggingError) {
      console.info("Chyba logovani byla detekovana, preskakuji odeslani.");
      return;
    }

    if (prefix == null)
      prefix = "Chyba";

    var cesta = path != null ? ", cesta: " + path : "";
    var chyba = null;

    if (error == null) {
      chyba = {
        "error": prefix + cesta,
        "stack": stacktrace
      }
    } else if (error.response == null) {
      chyba = {
        "error": prefix + cesta + ": " + error.toString(),
        "stack": stacktrace
      }
    } else {
      var status = error.response.status != null ? error.response.status : "";
      var data = error.response.data != null ? error.response.data : "";
      var mezera = status != "" && data != "" ? " " : "";
      var cesta = path != null ? ", cesta: " + path : "";
      chyba = {
        "error": prefix + " " + status + mezera + data + cesta + ": " + error.toString(),
        "stack": stacktrace
      }
    }

    isLoggingError = true; // Označíme, že logujeme
    try {
      console.error(chyba.error);

      this.getAuthenticationStatus( response => {
        if (response.data != null && response.data.authenticationGranted == true) {
          setTimeout(() => {
            axios
              .post("/" + process.env.VUE_APP_API_SEC_URL + API_PATH_LOG_POWEB_ERROR, chyba, {
                headers: {
                  "Content-Type": "application/json",
                },
              })
              .catch(error => {
                console.error("Chyba odeslani logu: ", error, path);
              });
          }, 50);
        }else{
          console.info("Uzivatel neni prihlasen, logovani preskoceno.");
        }
      } );
    } catch (err) {
      console.error("Chyba pri odeslani logu:", err);
    } finally {
      isLoggingError = false; // Po dokončení logování resetujeme flag
    }
  }

}
